import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  InputAdornment,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { supabase } from '../../config/supabase';
import { useView } from '../../contexts/ViewContext';
import { useNavigate } from 'react-router-dom';
import React from 'react';

/**
 * UserSelectorModal Component
 * Allows super admins to select which user to view as
 * 
 * @param {boolean} open - Modal open state
 * @param {function} onClose - Close handler
 * @param {string} userType - 'patient', 'sponsor', or 'clinic_staff'
 * @param {function} onSelection - Callback fired when a selection is made
 */
const UserSelectorModal = ({ open, onClose, userType, onSelection }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const { enterViewMode } = useView();
  const navigate = useNavigate();

  // Fetch users when modal opens
  useEffect(() => {
    if (!open || !userType) return;

    async function fetchUsers() {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select(`
            id,
            first_name,
            last_name,
            email,
            tenant_role,
            tenant_id,
            tenants:tenant_id (
              name
            )
          `)
          .eq('tenant_role', userType)
          .order('first_name');

        if (error) throw error;

        setUsers(data || []);
        setFilteredUsers(data || []);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, [open, userType]);

  // Filter users based on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = users.filter(user =>
      user.first_name?.toLowerCase().includes(term) ||
      user.last_name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.tenants?.name?.toLowerCase().includes(term)
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleSelectUser = (user) => {
    const userName = `${user.first_name} ${user.last_name}`;
    const tenantName = user.tenants?.name || 'Unknown Clinic';

    // Enter view mode
    enterViewMode(
      userType,
      user.id,
      userName,
      user.tenant_id,
      tenantName
    );

    // Navigate to appropriate dashboard
    const dashboardMap = {
      patient: '/dashboard',
      sponsor: '/sponsor-dashboard',
      clinic_staff: '/clinic-staff-dashboard'
    };

    navigate(dashboardMap[userType] || '/dashboard');

    // Notify parent of selection
    if (onSelection) {
      onSelection();
    }

    onClose();
  };

  const getUserTypeLabel = () => {
    const labels = {
      patient: 'Patient',
      sponsor: 'Sponsor',
      clinic_staff: 'Clinic Staff'
    };
    return labels[userType] || 'User';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Select {getUserTypeLabel()} to View As
        </Typography>
        <Typography variant="body2" component="div" color="text.secondary">
          Choose a {getUserTypeLabel().toLowerCase()} to view their dashboard
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Search Box */}
        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Search ${getUserTypeLabel().toLowerCase()}s...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* User List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'No users found' : `No ${getUserTypeLabel().toLowerCase()}s available`}
            </Typography>
          </Box>
        ) : (
          <List sx={{ pt: 0 }}>
            {filteredUsers.map((user, index) => (
              <React.Fragment key={user.id}>
                {index > 0 && <Divider component="li" />}
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleSelectUser(user)}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light' }}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" component="div" sx={{ fontWeight: 600 }}>
                          {user.first_name} {user.last_name}
                        </Typography>
                      }
                      secondary={
                        <Box component="span">
                          <Typography variant="caption" component="span" color="text.secondary" display="block">
                            {user.email}
                          </Typography>
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <BusinessIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                            <Typography variant="caption" component="span" color="text.secondary">
                              {user.tenants?.name || 'No Clinic'}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <Chip
                      label={user.tenant_role}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserSelectorModal;
